import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken, postAPIwithToken, putAPIwithToken, deleteAPIwithToken } from '../utils/fetchApi';
import type { Incentive, IncentiveFilters, CreateIncentiveForm, BackendIncentive, DistType } from '../types/incentive';
import { getTerritoriesAPI } from './locations';

let cachedTerritoryMap: Record<string | number, string> | null = null;

export const fetchTerritoryMap = async (): Promise<Record<string | number, string>> => {
  if (cachedTerritoryMap) return cachedTerritoryMap;
  try {
    const res = await getTerritoriesAPI();
    const rawList = res?.data || res?.territories || (Array.isArray(res) ? res : []);
    const tMap: Record<string | number, string> = {};
    if (Array.isArray(rawList)) {
      rawList.forEach((t: any) => {
        if (t.id && t.name) {
          tMap[t.id] = t.name;
          tMap[String(t.id)] = t.name;
        }
      });
    }
    cachedTerritoryMap = tMap;
    return tMap;
  } catch (err) {
    console.warn('Could not fetch territories map for incentive:', err);
    return {};
  }
};

export const mapBackendIncentiveToFrontend = (
  item: BackendIncentive | any,
  territoryMap?: Record<string | number, string>
): Incentive => {
  if (!item) {
    return {
      id: '',
      code: '-',
      name: '-',
      zone: '-',
      city: '-',
      zoneCity: '-',
      distType: '-',
      product: '-',
      payout: 'CREDIT',
      target: '0',
      targetQuantity: 0,
      reward: '₹0',
      rewardAmount: 0,
      unitType: 'Litre (L)',
      startDate: '',
      endDate: '',
      validity: '-',
      period: '-',
      status: 'Inactive',
    };
  }

  const codeStr = item.incentiveCode || item.code || String(item.id);
  const zoneName = item.zoneName || (typeof item.zone === 'object' && item.zone ? item.zone.name : typeof item.zone === 'string' ? item.zone : '');
  const cityName = item.cityName || (typeof item.city === 'object' && item.city ? item.city.name : typeof item.city === 'string' ? item.city : '');

  const territoryId =
    item.territory?.id ||
    (item as any).territoryId ||
    (item as any).territory_id ||
    (typeof item.city === 'object' && item.city?.territoryId);

  const territoryName =
    item.territory?.name ||
    (item as any).territoryName ||
    (territoryMap && territoryId && territoryMap[territoryId] ? territoryMap[territoryId] : '') ||
    '';

  const areaName = item.area?.name || (item as any).areaName || '';

  const locationParts: string[] = [];
  if (zoneName) locationParts.push(zoneName);
  if (cityName) locationParts.push(cityName);
  if (territoryName) locationParts.push(territoryName);

  const zoneCityStr = locationParts.length > 0 ? locationParts.join(' / ') : (cityName || zoneName || '-');

  const productName =
    item.productDisplayName ||
    item.productName ||
    (typeof item.product === 'object' && item.product
      ? item.product.displayName || item.product.name || '-'
      : typeof item.product === 'string'
      ? item.product
      : item.productId ? `Product #${item.productId}` : '-');

  const sapCodeStr =
    item.productSapCode ||
    (typeof item.product === 'object' && item.product?.sapCode
      ? item.product.sapCode
      : (item as any).sapCode || '-');

  const rawDistTypeId =
    item.distributorTypeId ||
    (item as any).distributor_type_id ||
    (typeof item.distributorType === 'object' && item.distributorType ? item.distributorType.id : undefined);

  const distTypeIdNum = rawDistTypeId ? Number(rawDistTypeId) : undefined;

  const distTypeName: DistType =
    item.distributorTypeName ||
    (typeof item.distributorType === 'object' && item.distributorType?.name
      ? item.distributorType.name
      : typeof item.distributorType === 'string'
      ? item.distributorType
      : item.distributorTypeId === 1
      ? 'GT'
      : item.distributorTypeId === 2
      ? 'MT'
      : item.distributorTypeId === 3
      ? 'Distributor + Dealer'
      : '-');

  const startDateFormatted = item.startDate
    ? new Date(item.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const endDateFormatted = item.endDate
    ? new Date(item.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const validityStr = startDateFormatted && endDateFormatted
    ? `${startDateFormatted} - ${endDateFormatted}`
    : startDateFormatted || endDateFormatted || '-';

  const periodStr = item.startDate && item.endDate
    ? `${new Date(item.startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })} - ${new Date(item.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}`
    : validityStr;

  const createdOnStr = item.createdAt
    ? new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  const extendedOnStr = item.updatedAt && item.createdAt && item.updatedAt !== item.createdAt
    ? new Date(item.updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  let status: 'Active' | 'Inactive' | 'Expired' | 'Upcoming' = 'Active';
  const statusUpper = item.status ? String(item.status).toUpperCase() : '';
  if (statusUpper === 'UPCOMING') {
    status = 'Upcoming';
  } else if (statusUpper === 'EXPIRED') {
    status = 'Expired';
  } else if (statusUpper === 'INACTIVE' || String(item.status) === '0' || item.status === 0) {
    status = 'Inactive';
  } else if (statusUpper === 'ACTIVE' || String(item.status) === '1' || item.status === 1) {
    status = 'Active';
  } else if (item.endDate && new Date(item.endDate) < new Date()) {
    status = 'Expired';
  } else if (item.startDate && new Date(item.startDate) > new Date()) {
    status = 'Upcoming';
  }

  let citiesList: string[] = [];
  if (cityName) {
    citiesList = [cityName];
  } else if (Array.isArray((item as any).cities) && (item as any).cities.length > 0) {
    citiesList = (item as any).cities
      .map((c: any) => (typeof c === 'string' ? c : c.name || c.cityName || c.city?.name || ''))
      .filter(Boolean);
  } else if (item.cityId) {
    citiesList = [`City #${item.cityId}`];
  } else {
    citiesList = [];
  }

  const targetQty = Number(item.targetQuantity ?? item.purchaseQuantity ?? (item as any).target ?? 0);
  const rawReward = item.rewardValue ?? item.rewardAmount ?? item.freeQuantity ?? (item as any).reward ?? 0;
  const rewardVal = Number(rawReward);
  const payoutStr = (item.rewardType || item.payoutType || item.payout || 'CREDIT').toUpperCase() === 'CASH' ? 'Cash' : 'Credit';

  return {
    id: codeStr,
    code: codeStr,
    incentiveCode: codeStr,
    sapCode: sapCodeStr,
    name: item.name || 'Untitled Incentive',
    zone: zoneName,
    city: cityName,
    territory: territoryName,
    area: areaName,
    zoneCity: zoneCityStr,
    distType: distTypeName,
    distributorTypeId: distTypeIdNum,
    product: productName,
    productId: item.productId || (typeof item.product === 'object' ? item.product?.id : undefined),
    zoneId: item.zoneId,
    cityId: item.cityId,
    cityIds: item.cityId ? [Number(item.cityId)] : [],
    payout: payoutStr,
    target: targetQty ? `${targetQty}` : '0',
    targetQuantity: targetQty,
    reward: rewardVal ? `₹${rewardVal.toLocaleString('en-IN')}` : '₹0',
    rewardAmount: rewardVal,
    unitType: item.unitType || 'Litre (L)',
    unitThreshold: item.unitThreshold || '1L',
    startDate: item.startDate || '',
    endDate: item.endDate || '',
    validity: validityStr,
    period: periodStr,
    status,
    createdAt: item.createdAt,
    createdBy: item.creatorName || (typeof item.creator === 'object' && item.creator?.name ? item.creator.name : (item as any).createdByName || (item.createdBy ? `User #${item.createdBy}` : '-')),
    createdOn: createdOnStr,
    extendedBy: (item as any).extendedBy || '-',
    extendedOn: extendedOnStr,
    duration: validityStr,
    citiesList,
    description: item.description || '',
    crateMapping: item.crateUnits ? `${item.crateUnits} Units/Crate` : '',
    rawBackendData: item,
  };
};

export const getIncentives = async (filters?: Partial<IncentiveFilters>) => {
  const territoryMap = await fetchTerritoryMap();

  try {
    const res = await getAPIwithToken<any>(ENDPOINTS.GET_INCENTIVES);
    const rawList = res?.data || res?.incentives || (Array.isArray(res) ? res : []);

    if (Array.isArray(rawList)) {
      // Group by incentiveCode so multiple city entries for the same incentive are combined into one
      const groupedMap = new Map<string, any[]>();
      rawList.forEach((rawItem: any) => {
        const groupKey = rawItem.incentiveCode || rawItem.code || String(rawItem.id);
        if (!groupedMap.has(groupKey)) {
          groupedMap.set(groupKey, []);
        }
        groupedMap.get(groupKey)!.push(rawItem);
      });

      let mappedIncentives: Incentive[] = [];

      groupedMap.forEach((items, groupKey) => {
        const primary = items[0];
        const mapped = mapBackendIncentiveToFrontend(primary, territoryMap);

        const citiesSet = new Set<string>();
        const cityIdsSet = new Set<number>();

        items.forEach((it) => {
          const cName = it.cityName || (typeof it.city === 'object' && it.city ? it.city.name : typeof it.city === 'string' ? it.city : '');
          if (cName) citiesSet.add(cName);
          if (it.cityId) cityIdsSet.add(Number(it.cityId));
          if (Array.isArray(it.cities)) {
            it.cities.forEach((c: any) => {
              const name = typeof c === 'string' ? c : c.name || c.cityName;
              if (name) citiesSet.add(name);
              if (c.id) cityIdsSet.add(Number(c.id));
            });
          }
        });

        const mergedCities = Array.from(citiesSet);
        const mergedCityIds = Array.from(cityIdsSet);

        mapped.id = groupKey;
        mapped.code = groupKey;
        mapped.incentiveCode = groupKey;

        if (mergedCities.length > 0) {
          mapped.citiesList = mergedCities;
          mapped.city = mergedCities.join(', ');
          const zonePrefix = mapped.zone && mapped.zone !== '-' ? mapped.zone : '';
          mapped.zoneCity = zonePrefix ? `${zonePrefix} / ${mergedCities.join(', ')}` : mergedCities.join(', ');
        }
        if (mergedCityIds.length > 0) {
          mapped.cityIds = mergedCityIds;
        }

        mappedIncentives.push(mapped);
      });

      if (filters?.search) {
        const q = filters.search.toLowerCase();
        mappedIncentives = mappedIncentives.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            i.code.toLowerCase().includes(q) ||
            i.product.toLowerCase().includes(q)
        );
      }
      if (filters?.location && filters.location !== 'All Locations' && filters.location !== 'All Regions') {
        const locVal = filters.location.toLowerCase();
        mappedIncentives = mappedIncentives.filter(
          (i) => i.zone.toLowerCase().includes(locVal) || i.city.toLowerCase().includes(locVal) || i.zoneCity.toLowerCase().includes(locVal)
        );
      }
      if (filters?.distributorType && filters.distributorType !== 'All Types') {
        const distVal = filters.distributorType.toLowerCase();
        mappedIncentives = mappedIncentives.filter(
          (i) => String(i.distType).toLowerCase().includes(distVal) || distVal.includes(String(i.distType).toLowerCase())
        );
      }
      if (filters?.status && filters.status !== 'Status' && filters.status !== 'all') {
        const statusVal = filters.status.toLowerCase();
        mappedIncentives = mappedIncentives.filter((i) => i.status.toLowerCase() === statusVal);
      }

      const total = res?.pagination?.totalCount || res?.pagination?.total || mappedIncentives.length;
      return {
        success: true,
        data: mappedIncentives,
        total,
        page: filters?.page || 1,
        limit: filters?.limit || 20,
      };
    }
  } catch (err) {
    console.error('API GET /api/v1/erp/incentives failed:', err);
    throw err;
  }

  return {
    success: true,
    data: [],
    total: 0,
    page: filters?.page || 1,
    limit: filters?.limit || 20,
  };
};

export const getIncentiveById = async (idOrCode: string | number): Promise<Incentive | null> => {
  try {
    const res = await getAPIwithToken<any>(ENDPOINTS.GET_INCENTIVE_BY_ID(idOrCode));
    const rawData = res?.data || res;
    const territoryMap = await fetchTerritoryMap();

    if (Array.isArray(rawData) && rawData.length > 0) {
      const primary = rawData[0];
      const mapped = mapBackendIncentiveToFrontend(primary, territoryMap);
      const citiesSet = new Set<string>();
      const cityIdsSet = new Set<number>();

      rawData.forEach((it) => {
        const cName = it.cityName || (typeof it.city === 'object' && it.city ? it.city.name : typeof it.city === 'string' ? it.city : '');
        if (cName) citiesSet.add(cName);
        if (it.cityId) cityIdsSet.add(Number(it.cityId));
      });

      if (citiesSet.size > 0) {
        const mergedCities = Array.from(citiesSet);
        mapped.citiesList = mergedCities;
        mapped.city = mergedCities.join(', ');
        const zonePrefix = mapped.zone && mapped.zone !== '-' ? mapped.zone : '';
        mapped.zoneCity = zonePrefix ? `${zonePrefix} / ${mergedCities.join(', ')}` : mergedCities.join(', ');
      }
      if (cityIdsSet.size > 0) {
        mapped.cityIds = Array.from(cityIdsSet);
      }
      return mapped;
    }

    if (rawData && (rawData.id !== undefined || rawData.incentiveCode || rawData.name)) {
      return mapBackendIncentiveToFrontend(rawData, territoryMap);
    }
  } catch (err) {
    console.error(`API GET /api/v1/erp/incentives/${idOrCode} failed:`, err);
  }
  return null;
};

export const createIncentive = async (formData: CreateIncentiveForm): Promise<Incentive> => {
  let resolvedCityIds: number[] = [];
  if (Array.isArray(formData.cityIds) && formData.cityIds.length > 0) {
    resolvedCityIds = formData.cityIds.map(Number).filter((n) => !isNaN(n) && n > 0);
  } else if (Array.isArray(formData.cityId)) {
    resolvedCityIds = (formData.cityId as any[]).map(Number).filter((n) => !isNaN(n) && n > 0);
  } else if (formData.cityId && !isNaN(Number(formData.cityId)) && Number(formData.cityId) > 0) {
    resolvedCityIds = [Number(formData.cityId)];
  }

  const cityIdPayload = resolvedCityIds.length > 0 ? resolvedCityIds : [1];

  const payload: Record<string, any> = {
    source: 'Desktop',
    name: formData.incentiveName,
    description: formData.description || formData.incentiveName,
    distributorTypeId: formData.distributorTypeId
      ? Number(formData.distributorTypeId)
      : formData.distributorType
      ? Number(formData.distributorType) || 3
      : 3,
    productId: formData.productId ? Number(formData.productId) : 2,
    cityId: cityIdPayload,
    zoneId: formData.zoneId ? Number(formData.zoneId) : 2,
    minAmount: Number(formData.minAmount || 0),
    maxAmount: formData.maxAmount ? Number(formData.maxAmount) : null,
    rewardType: (formData.payoutType || formData.rewardType || 'CREDIT').toUpperCase() === 'CASH' ? 'CASH' : 'CREDIT',
    rewardValue: Number(formData.incentiveRate || formData.rewardValue || 1),
    incentivePercent: Number(formData.incentivePercent || 4.00),
    targetQuantity: Number(formData.targetQuantity || 250.00),
    startDate: formData.startDate
      ? formData.startDate.includes('T')
        ? formData.startDate
        : `${formData.startDate}T00:00:00.000Z`
      : new Date().toISOString(),
    endDate: formData.endDate
      ? formData.endDate.includes('T')
        ? formData.endDate
        : `${formData.endDate}T23:59:59.000Z`
      : new Date().toISOString(),
    priority: formData.priority !== undefined ? Number(formData.priority) : 1,
    status: formData.status !== undefined ? Number(formData.status) : 1,
  };

  const res = await postAPIwithToken<any>(ENDPOINTS.CREATE_INCENTIVE, payload);
  const data = res?.data || res;
  return mapBackendIncentiveToFrontend(data);
};

export const updateIncentive = async (
  idOrCodeOrForm: string | number | CreateIncentiveForm,
  formDataParam?: CreateIncentiveForm
): Promise<Incentive> => {
  const isKeyGiven = typeof idOrCodeOrForm === 'string' || typeof idOrCodeOrForm === 'number';
  const formData = isKeyGiven ? formDataParam! : (idOrCodeOrForm as CreateIncentiveForm);
  const codeOrId = isKeyGiven
    ? idOrCodeOrForm
    : formData.incentiveCode || formData.code || formData.id;

  if (!codeOrId) {
    return createIncentive(formData);
  }

  let resolvedCityIds: number[] = [];
  if (Array.isArray(formData.cityIds) && formData.cityIds.length > 0) {
    resolvedCityIds = formData.cityIds.map(Number).filter((n) => !isNaN(n) && n > 0);
  } else if (Array.isArray(formData.cityId)) {
    resolvedCityIds = (formData.cityId as any[]).map(Number).filter((n) => !isNaN(n) && n > 0);
  } else if (formData.cityId && !isNaN(Number(formData.cityId)) && Number(formData.cityId) > 0) {
    resolvedCityIds = [Number(formData.cityId)];
  }

  const payload: Record<string, any> = {
    source: 'Desktop',
    incentiveCode: String(codeOrId),
  };
  if (formData.incentiveName) payload.name = formData.incentiveName;
  if (formData.description !== undefined) payload.description = formData.description;
  if (formData.distributorTypeId !== undefined || formData.distributorType !== undefined) {
    payload.distributorTypeId = Number(formData.distributorTypeId || formData.distributorType);
  }
  if (formData.productId) payload.productId = Number(formData.productId);
  
  if (resolvedCityIds.length > 0) {
    payload.cityId = resolvedCityIds;
  } else {
    payload.cityId = [1];
  }

  if (formData.zoneId) payload.zoneId = Number(formData.zoneId);
  if (formData.payoutType || formData.rewardType) {
    payload.rewardType = (formData.payoutType || formData.rewardType || 'CREDIT').toUpperCase() === 'CASH' ? 'CASH' : 'CREDIT';
  }
  if (formData.incentiveRate !== undefined || formData.rewardValue !== undefined) {
    payload.rewardValue = Number(formData.incentiveRate ?? formData.rewardValue);
  }
  if (formData.targetQuantity !== undefined) payload.targetQuantity = Number(formData.targetQuantity);
  if (formData.incentivePercent !== undefined) payload.incentivePercent = Number(formData.incentivePercent);
  if (formData.minAmount !== undefined) payload.minAmount = Number(formData.minAmount);
  if (formData.maxAmount !== undefined) payload.maxAmount = formData.maxAmount ? Number(formData.maxAmount) : null;
  if (formData.startDate) {
    payload.startDate = formData.startDate.includes('T') ? formData.startDate : `${formData.startDate}T00:00:00.000Z`;
  }
  if (formData.endDate) {
    payload.endDate = formData.endDate.includes('T') ? formData.endDate : `${formData.endDate}T23:59:59.000Z`;
  }
  if (formData.priority !== undefined) payload.priority = Number(formData.priority);
  if (formData.isDelete !== undefined) {
    const isInactive = formData.isDelete === true || formData.isDelete === 1 || String(formData.isDelete) === '1';
    payload.isDelete = isInactive ? 1 : 0;
  } else if ((formData as any).is_delete !== undefined) {
    const val = (formData as any).is_delete;
    const isInactive = val === true || val === 1 || String(val) === '1';
    payload.isDelete = isInactive ? 1 : 0;
  } else if (formData.status !== undefined) {
    const isInactive = String(formData.status).toLowerCase() === 'inactive' || formData.status === 0 || String(formData.status) === '0';
    payload.isDelete = isInactive ? 1 : 0;
  }

  const res = await putAPIwithToken<any>(ENDPOINTS.UPDATE_INCENTIVE(codeOrId), payload);
  const data = res?.data || res;
  return mapBackendIncentiveToFrontend(data);
};

export const removeIncentiveApi = async (idOrCode: number | string): Promise<boolean> => {
  try {
    const endpoint = ENDPOINTS.DELETE_INCENTIVE(idOrCode);
    const res = await deleteAPIwithToken<any>(endpoint);
    if (res && (res.status === 'success' || res.success)) {
      return true;
    }
  } catch (e) {
    console.warn('Backend delete incentive call failed:', e);
  }
  return true;
};

export const extendIncentive = async (
  idOrCode: number | string,
  extendedDays: number,
  remarks?: string
): Promise<Incentive> => {
  const existing = await getIncentiveById(idOrCode);
  let currentEndDate = existing?.endDate ? new Date(existing.endDate) : new Date();
  if (isNaN(currentEndDate.getTime())) {
    currentEndDate = new Date();
  }

  const newEndDate = new Date(currentEndDate.getTime() + extendedDays * 24 * 60 * 60 * 1000);
  const newEndDateIso = newEndDate.toISOString();

  const payload: Record<string, any> = {
    endDate: newEndDateIso,
  };
  if (remarks) {
    payload.remarks = remarks;
  }

  const endpoint = ENDPOINTS.UPDATE_INCENTIVE(idOrCode);
  const res = await putAPIwithToken<any>(endpoint, payload);
  const data = res?.data || res;
  return mapBackendIncentiveToFrontend(data);
};

// ==================== Template, Bulk Upload & Download APIs ====================

// 1. Download Incentive Template API (GET /api/v1/erp/incentives/template)
export const downloadIncentiveTemplateAPI = async (): Promise<void> => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const token: string | null = localStorage.getItem('gowardhan_erp_token');
  const endpoint = ENDPOINTS.INCENTIVE_TEMPLATE.startsWith('/')
    ? ENDPOINTS.INCENTIVE_TEMPLATE
    : `/${ENDPOINTS.INCENTIVE_TEMPLATE}`;

  const response = await fetch(`${cleanBase}${endpoint}`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    let errorMsg = `Template download failed with status ${response.status}`;
    try {
      const errJson = await response.json();
      if (errJson.message) errorMsg = errJson.message;
    } catch {
      // Ignore parse error
    }
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'incentive_upload_template.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// 2. Upload Incentive Bulk Excel API (POST /api/v1/erp/incentives/upload)
export const uploadIncentivesBulkAPI = async (file: File): Promise<any> => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const token: string | null = localStorage.getItem('gowardhan_erp_token');
  const endpoint = ENDPOINTS.INCENTIVE_UPLOAD.startsWith('/')
    ? ENDPOINTS.INCENTIVE_UPLOAD
    : `/${ENDPOINTS.INCENTIVE_UPLOAD}`;

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${cleanBase}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
    body: formData,
  });

  const resJson = await response.json();
  if (!response.ok) {
    throw new Error(resJson.message || `Upload failed with status ${response.status}`);
  }

  return resJson;
};

// 3. Download Incentive Data API (GET /api/v1/erp/incentives/download)
export const downloadIncentivesAPI = async (): Promise<void> => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const token: string | null = localStorage.getItem('gowardhan_erp_token');
  const endpoint = ENDPOINTS.INCENTIVE_DOWNLOAD.startsWith('/')
    ? ENDPOINTS.INCENTIVE_DOWNLOAD
    : `/${ENDPOINTS.INCENTIVE_DOWNLOAD}`;

  const response = await fetch(`${cleanBase}${endpoint}`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    let errorMsg = `Download failed with status ${response.status}`;
    try {
      const errJson = await response.json();
      if (errJson.message) errorMsg = errJson.message;
    } catch {
      // Ignore parse error
    }
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `incentives_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

